import type { OverflowMenuItems } from '../../types';
import type { ReactNode } from 'react';
import { Icon, Body, Card, Stack, Col } from '@workramp/ui';
import { useTheme } from 'styled-components';
import { I18n } from '~/common/explicit-exports';
import { FooterWrapper, FullHeightRow } from '../../styles';
import { CardOverflowMenu } from '../OverflowCardMenu';
import { CardDescription, CardTitle } from '../CardText';

const { DEFAULT_COVER_IMAGES } = window.APP_CONSTANTS;

export type FolderCardProps = {
  name: string;
  imageSrc?: string | null;
  description: string;
  numItems: number;
  onPress?: () => void;
  overflowMenuItems?: OverflowMenuItems;
  actions?: ReactNode;
};

export const FolderCard = ({
  name,
  imageSrc,
  description,
  numItems,
  onPress,
  overflowMenuItems,
  actions,
}: FolderCardProps) => {
  const ITEMS = I18n.t('js.learner.libraries.items', {
    numItems,
  });
  const ITEM = I18n.t('js.learner.libraries.item');
  const showOverflowMenu = Boolean(Object.keys(overflowMenuItems || {}).length);

  const theme = useTheme();
  return (
    <Card onPress={onPress} data-testid="folder-card">
      {imageSrc && (
        <Card.Hero
          src={imageSrc}
          errorImage={DEFAULT_COVER_IMAGES.GUIDE}
          icon={<Icon color={theme.colors.on[900]} name="folder-closed:regular" size="lg" />}
        />
      )}
      <Card.Container data-testid="library-card">
        <Card.Content>
          <Card.Body>
            <Stack size={4}>
              {!imageSrc && <Card.Icon iconColor={theme.colors.on[900]} iconName="folder-closed:regular" />}
              <Stack size={2}>
                <CardTitle text={name} />
                <CardDescription text={description} color={theme.colors.on[500]} />
              </Stack>
            </Stack>
          </Card.Body>
        </Card.Content>
      </Card.Container>
      <Card.Footer size={0}>
        <FooterWrapper>
          <FullHeightRow align="center">
            <Col>
              <Body color={theme.colors.on[600]} level="md" unselectable="on">
                {numItems === 1 ? ITEM : ITEMS}
              </Body>
            </Col>
            {(showOverflowMenu || actions) && (
              <Col xs="auto">
                {actions}
                {showOverflowMenu && <CardOverflowMenu overflowMenuItems={overflowMenuItems} />}
              </Col>
            )}
          </FullHeightRow>
        </FooterWrapper>
      </Card.Footer>
    </Card>
  );
};
